import { useState } from 'react';
import { Box, Flex, Image, Text } from '@chakra-ui/react';
import { PlayIcon } from '@phosphor-icons/react';
import { Container, DisplayHeading, Eyebrow, Stamp } from '@/travo/components/ui/postmark';
import DemoVideoDialog from '@/travo/components/ui/dialogs/DemoVideoDialog';
import { useCopy } from '@/travo/i18n';
import { HARD_SHADOW, INK_RULE, PILL_BUTTON } from '@/travo/theme/constants';
import mapPhoto from '@/travo/assets/images/home/map_demo.webp';
import appIcon from '@/travo/assets/icons/logo-256.webp';

const ADDRESS_LINE = { borderBottom: INK_RULE.line, py: '10px' } as const;

/** Postcard: map photo on the front, friends message and demo video on the back. */
const FriendsPostcard = () => {
  const copy = useCopy();
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  return (
    <Container as="section" pb={{ base: '80px', md: '112px' }}>
      <Flex wrap="wrap" bg="card" border="2px solid" borderColor="ink" boxShadow={HARD_SHADOW.card}>
        <Box flex="1 1 520px" minW={0} minH={{ base: '280px', md: '420px' }} position="relative">
          <Image src={mapPhoto} alt={copy.friends.mapAlt} position="absolute" inset={0} w="100%" h="100%" objectFit="cover" loading="lazy" />
          <Text
            position="absolute"
            left="20px"
            bottom="20px"
            right="20px"
            w="fit-content"
            bg="card"
            border="2px solid"
            borderColor="ink"
            fontFamily="mono"
            fontSize="12px"
            letterSpacing="0.12em"
            px="12px"
            py="6px"
          >
            {copy.friends.caption}
          </Text>
        </Box>

        <Flex
          direction="column"
          gap="22px"
          flex="1 1 440px"
          minW={0}
          p={{ base: '24px', md: '40px' }}
          borderLeft={{ base: 'none', md: INK_RULE.dashed }}
          borderTop={{ base: INK_RULE.dashed, md: 'none' }}
        >
          <Flex justify="space-between" align="flex-start" gap="16px">
            <Eyebrow pt="6px">{copy.friends.eyebrow}</Eyebrow>
            <Stamp w="96px" flex="none" edge="paper" frame="card" margin="10px" innerPadding="6px" transform="rotate(4deg)" filter="none">
              <Image src={appIcon} alt="" w="100%" aspectRatio="1" loading="lazy" />
            </Stamp>
          </Flex>
          <DisplayHeading fontSize={{ base: '30px', md: '44px' }} lineHeight="1.2">
            {copy.friends.title}
          </DisplayHeading>
          <Text color="inkSoft">{copy.friends.body}</Text>
          <Box fontFamily="mono" fontSize="14px">
            <Text {...ADDRESS_LINE}>{copy.friends.to}</Text>
            <Text {...ADDRESS_LINE}>{copy.friends.from}</Text>
          </Box>
          <Box as="button" {...PILL_BUTTON} alignSelf="flex-start" mt="6px" onClick={() => setIsVideoOpen(true)}>
            <PlayIcon size={18} weight="fill" aria-hidden />
            {copy.friends.cta}
          </Box>
        </Flex>
      </Flex>

      <DemoVideoDialog open={isVideoOpen} onClose={() => setIsVideoOpen(false)} />
    </Container>
  );
};

export default FriendsPostcard;
